import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { adminService } from "../../api/services/adminService";
import { userService } from "../../api/services/userService";

// Initial state
const initialState = {
  user: null,
  admin: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isAdmin: false,
  isLoading: false,
  error: null,
};

// Helper to check if we're in browser environment
const isBrowser = typeof window !== "undefined";

// Helper to load initial state from localStorage
const loadInitialState = () => {
  if (!isBrowser) return initialState;

  const isAdmin = localStorage.getItem("isAdminAuthenticated") === "true";
  const token = localStorage.getItem("accessToken") || null;
  const refreshToken = localStorage.getItem("refreshToken") || null;

  if (isAdmin) {
    const adminData = localStorage.getItem("admin");
    return {
      ...initialState,
      admin: adminData ? JSON.parse(adminData) : null,
      token,
      refreshToken,
      isAuthenticated: !!token,
      isAdmin: true,
    };
  } else {
    const userData = localStorage.getItem("user");
    return {
      ...initialState,
      user: userData ? JSON.parse(userData) : null,
      token,
      refreshToken,
      isAuthenticated: !!token,
      isAdmin: false,
    };
  }
};

// Admin login
export const adminLogin = createAsyncThunk(
  "auth/adminLogin",
  async (credentials, { rejectWithValue }) => {
    try {
      console.log("Admin login thunk - credentials:", credentials);
      const response = await adminService.login(credentials);
      console.log("Admin login thunk - response:", response);

      if (response.success) {
        return {
          admin: response.admin,
          token: response.token,
          refreshToken: response.refreshToken,
          expiresIn: response.expiresIn,
        };
      }
      throw new Error("Login failed");
    } catch (error) {
      console.error(
        "Admin login thunk - error:",
        error.response?.data || error.message,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.errors?.[0]?.msg ||
          "Login failed",
      );
    }
  },
);

// User login
export const userLogin = createAsyncThunk(
  "auth/userLogin",
  async (credentials, { rejectWithValue }) => {
    try {
      console.log("User login thunk - credentials:", credentials);
      const response = await userService.login(credentials);
      console.log("User login thunk - full response:", response);

      // Log the structure to debug
      console.log("Response structure:", {
        hasData: !!response.data,
        hasUser: !!response.user,
        hasToken: !!response.token,
        responseKeys: Object.keys(response),
      });

      // Return the response as-is - the reducer will handle different structures
      return response;
    } catch (error) {
      console.error("User login thunk - error:", error);

      // Extract error message from different error structures
      let errorMessage = "Login failed";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        errorMessage = error.response.data.errors[0]?.msg || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return rejectWithValue(errorMessage);
    }
  },
);

// User register
export const userRegister = createAsyncThunk(
  "auth/userRegister",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await userService.register(userData);
      console.log("User register response:", response);
      return response;
    } catch (error) {
      console.error("User register error:", error);
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.errors?.[0]?.msg ||
          "Registration failed",
      );
    }
  },
);

// Admin logout
export const adminLogout = createAsyncThunk(
  "auth/adminLogout",
  async (_, { rejectWithValue }) => {
    try {
      await adminService.logout();
      return { success: true };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Logout failed");
    }
  },
);

// User logout
export const userLogout = createAsyncThunk(
  "auth/userLogout",
  async (_, { rejectWithValue }) => {
    try {
      await userService.logout();
      return { success: true };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Logout failed");
    }
  },
);

// Get current admin profile
export const getAdminProfile = createAsyncThunk(
  "auth/getAdminProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.getProfile();
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get profile",
      );
    }
  },
);

// Get current user profile
export const getUserProfile = createAsyncThunk(
  "auth/getUserProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await userService.getProfile();
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get profile",
      );
    }
  },
);

// Refresh token
export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (refreshTokenValue, { rejectWithValue }) => {
    try {
      const response = await adminService.refreshToken(refreshTokenValue);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Token refresh failed",
      );
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState: loadInitialState(),
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetAuth: (state) => {
      state.user = null;
      state.admin = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.isAdmin = false;
      state.error = null;

      if (isBrowser) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        localStorage.removeItem("admin");
        localStorage.removeItem("isAdminAuthenticated");
      }
    },
    markEmailVerified: (state) => {
      if (state.user) {
        state.user.emailVerifiedAt = new Date().toISOString();
        // Also update localStorage copy if present
        if (isBrowser) {
          localStorage.setItem("user", JSON.stringify(state.user));
        }
      }
      if (state.admin) {
        state.admin.emailVerifiedAt = new Date().toISOString();
        if (isBrowser) {
          localStorage.setItem("admin", JSON.stringify(state.admin));
        }
      }
    },
    setCredentials: (state, action) => {
      const { user, admin, token, refreshToken } = action.payload;

      if (admin) {
        state.user = null;
        state.admin = admin;
        state.token = token;
        state.refreshToken = refreshToken;
        state.isAuthenticated = true;
        state.isAdmin = true;

        if (isBrowser) {
          localStorage.setItem("accessToken", token);
          localStorage.setItem("refreshToken", refreshToken);
          localStorage.setItem("admin", JSON.stringify(admin));
          localStorage.setItem("isAdminAuthenticated", "true");
          localStorage.removeItem("user");
        }
      } else if (user) {
        state.admin = null;
        state.user = user;
        state.token = token;
        state.refreshToken = refreshToken;
        state.isAuthenticated = true;
        state.isAdmin = false;

        if (isBrowser) {
          localStorage.setItem("accessToken", token);
          localStorage.setItem("refreshToken", refreshToken);
          localStorage.setItem("user", JSON.stringify(user));
          localStorage.setItem("isAdminAuthenticated", "false");
          localStorage.removeItem("admin");
        }
      }

      state.error = null;
    },
    logout: (state) => {
      state.user = null;
      state.admin = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.isAdmin = false;
      state.error = null;

      if (isBrowser) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        localStorage.removeItem("admin");
        localStorage.removeItem("isAdminAuthenticated");
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Admin Login
      .addCase(adminLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(adminLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.admin = action.payload.admin;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthenticated = true;
        state.isAdmin = true;
        state.error = null;

        if (isBrowser) {
          localStorage.setItem("accessToken", state.token);
          localStorage.setItem("refreshToken", state.refreshToken);
          localStorage.setItem("admin", JSON.stringify(state.admin));
          localStorage.setItem("isAdminAuthenticated", "true");
          localStorage.removeItem("user");
        }

        console.log("Admin login fulfilled:", {
          admin: state.admin,
          token: !!state.token,
          isAuthenticated: state.isAuthenticated,
          isAdmin: state.isAdmin,
        });
      })
      .addCase(adminLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.isAdmin = false;
        console.log("Admin login rejected:", action.payload);
      })

      // User Login
      .addCase(userLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(userLogin.fulfilled, (state, action) => {
        state.isLoading = false;

        // Handle different response structures
        let userData, tokenData, refreshTokenData;

        // Check if response has a data property
        if (action.payload?.data) {
          userData = action.payload.data.user || action.payload.data;
          tokenData =
            action.payload.data.token || action.payload.data.accessToken;
          refreshTokenData = action.payload.data.refreshToken;
        }
        // Check if response directly has user and token
        else if (action.payload?.user && action.payload?.token) {
          userData = action.payload.user;
          tokenData = action.payload.token;
          refreshTokenData = action.payload.refreshToken;
        }
        // Check if response is the user object with token in it
        else if (action.payload?.token) {
          userData = action.payload;
          tokenData = action.payload.token;
          refreshTokenData = action.payload.refreshToken;
        }
        // If response is just the user object
        else {
          userData = action.payload;
          tokenData = action.payload?.token || action.payload?.accessToken;
          refreshTokenData = action.payload?.refreshToken;
        }

        console.log("Processed user data:", {
          userData,
          tokenData,
          refreshTokenData,
        });

        if (!userData || !tokenData) {
          console.error("Invalid response structure:", action.payload);
          state.error = "Invalid response from server";
          state.isAuthenticated = false;
          return;
        }

        state.admin = null;
        state.user = userData;
        state.token = tokenData;
        state.refreshToken = refreshTokenData;
        state.isAuthenticated = true;
        state.isAdmin = false;
        state.error = null;

        if (isBrowser) {
          localStorage.setItem("accessToken", state.token);
          if (state.refreshToken) {
            localStorage.setItem("refreshToken", state.refreshToken);
          }
          localStorage.setItem("user", JSON.stringify(state.user));
          localStorage.setItem("isAdminAuthenticated", "false");
          localStorage.removeItem("admin");
        }

        console.log("User login fulfilled - final state:", {
          user: state.user,
          token: !!state.token,
          refreshToken: !!state.refreshToken,
          isAuthenticated: state.isAuthenticated,
          isAdmin: state.isAdmin,
        });
      })
      .addCase(userLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.isAdmin = false;
      })

      // User Register
      .addCase(userRegister.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(userRegister.fulfilled, (state, action) => {
        state.isLoading = false;

        // Handle different response structures
        let userData, tokenData, refreshTokenData;

        if (action.payload?.data) {
          userData = action.payload.data.user || action.payload.data;
          tokenData =
            action.payload.data.token || action.payload.data.accessToken;
          refreshTokenData = action.payload.data.refreshToken;
        } else {
          userData = action.payload.user || action.payload;
          tokenData = action.payload.token || action.payload.accessToken;
          refreshTokenData = action.payload.refreshToken;
        }

        state.admin = null;
        state.user = userData;
        state.token = tokenData;
        state.refreshToken = refreshTokenData;
        state.isAuthenticated = true;
        state.isAdmin = false;
        state.error = null;

        if (isBrowser) {
          localStorage.setItem("accessToken", state.token);
          localStorage.setItem("refreshToken", state.refreshToken);
          localStorage.setItem("user", JSON.stringify(state.user));
          localStorage.setItem("isAdminAuthenticated", "false");
          localStorage.removeItem("admin");
        }

        console.log("User register fulfilled:", {
          user: state.user,
          token: !!state.token,
          isAuthenticated: state.isAuthenticated,
          isAdmin: state.isAdmin,
        });
      })
      .addCase(userRegister.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        console.log("User register rejected:", action.payload);
      })

      // Admin Logout
      .addCase(adminLogout.fulfilled, (state) => {
        state.admin = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.isAdmin = false;

        if (isBrowser) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("admin");
          localStorage.removeItem("isAdminAuthenticated");
        }
      })
      .addCase(adminLogout.rejected, (state) => {
        state.admin = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.isAdmin = false;

        if (isBrowser) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("admin");
          localStorage.removeItem("isAdminAuthenticated");
        }
      })

      // User Logout
      .addCase(userLogout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.isAdmin = false;

        if (isBrowser) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          localStorage.removeItem("isAdminAuthenticated");
        }
      })
      .addCase(userLogout.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.isAdmin = false;

        if (isBrowser) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          localStorage.removeItem("isAdminAuthenticated");
        }
      })

      // Get Admin Profile
      .addCase(getAdminProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAdminProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        const responseData = action.payload?.data || action.payload;
        state.user = null;
        state.admin = responseData.admin || responseData.user || responseData;
        state.isAdmin = true;
        state.isAuthenticated = true;

        if (isBrowser) {
          localStorage.setItem("admin", JSON.stringify(state.admin));
          localStorage.setItem("isAdminAuthenticated", "true");
          localStorage.removeItem("user");
        }
      })
      .addCase(getAdminProfile.rejected, (state) => {
        state.isLoading = false;
        state.admin = null;
        state.isAdmin = false;
        state.isAuthenticated = false;
        state.token = null;
        state.refreshToken = null;

        if (isBrowser) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("admin");
          localStorage.removeItem("isAdminAuthenticated");
        }
      })

      // Get User Profile
      .addCase(getUserProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        const responseData = action.payload?.data || action.payload;
        state.admin = null;
        state.user = responseData.user || responseData;
        state.isAuthenticated = true;
        state.isAdmin = false;

        if (isBrowser) {
          localStorage.setItem("user", JSON.stringify(state.user));
          localStorage.setItem("isAdminAuthenticated", "false");
          localStorage.removeItem("admin");
        }
      })
      .addCase(getUserProfile.rejected, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.isAdmin = false;
        state.token = null;
        state.refreshToken = null;

        if (isBrowser) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          localStorage.removeItem("isAdminAuthenticated");
        }
      })

      // Refresh Token
      .addCase(refreshToken.fulfilled, (state, action) => {
        const responseData = action.payload?.data || action.payload;
        state.token = responseData.token || responseData.accessToken;
        state.refreshToken = responseData.refreshToken;
        state.isAuthenticated = true;

        if (isBrowser) {
          localStorage.setItem("accessToken", state.token);
          localStorage.setItem("refreshToken", state.refreshToken);
        }
      })
      .addCase(refreshToken.rejected, (state) => {
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.isAdmin = false;
        state.user = null;
        state.admin = null;

        if (isBrowser) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          localStorage.removeItem("admin");
          localStorage.removeItem("isAdminAuthenticated");
        }
      });
  },
});

export const {
  clearError,
  resetAuth,
  setCredentials,
  markEmailVerified,
  checkTokenExpiration,
  logout,
} = authSlice.actions;
export default authSlice.reducer;
